type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const KEY = "webscan2:backend-recovery:v2";

/** A failed WASM/WebGPU initialization stays failed inside ORT for this page's lifetime.
 * Remember one fallback across a reload, which also releases partially built models.
 * The marker belongs to this tab and backend selection, never to persisted settings.
 */
export class BackendRecovery {
  private readonly storage: StorageLike | null;

  constructor(storage: StorageLike | null) {
    this.storage = storage;
  }

  preferred(selection: string): "wasm" | null {
    if (!["auto", "webgpu", "webgl"].includes(selection)) return null;
    try {
      return this.storage?.getItem(KEY) === selection ? "wasm" : null;
    } catch {
      return null;
    }
  }

  arm(selection: string, backend: string | null, message: string, wasmAvailable: boolean): boolean {
    if (
      (backend !== "webgpu" && backend !== "webgl") ||
      !wasmAvailable ||
      !["auto", "webgpu", "webgl"].includes(selection) ||
      this.preferred(selection) ||
      !/no available backend|out of memory|memory allocation|device.{0,20}lost|webgl.{0,30}(context|support)|shader.{0,20}(fail|error)/i.test(
        message,
      )
    )
      return false;
    try {
      this.storage?.setItem(KEY, selection);
      // Without durable state a reload could repeat forever; leave the error visible.
      return this.storage?.getItem(KEY) === selection;
    } catch {
      return false;
    }
  }

  clear(): void {
    try {
      this.storage?.removeItem(KEY);
    } catch {
      /* storage unavailable */
    }
  }
}
